<template>
  <div class="riw-preview">
    <div class="riw-preview__top">
      <div class="riw-preview__cards" :style="{ '--cards': cards.length }">
        <div v-for="card in cards" :key="card.key" class="riw-summary-card">
          <div class="riw-summary-icon" :class="`riw-summary-icon--${card.color}`"><v-icon>{{ card.icon }}</v-icon></div>
          <div class="riw-preview__card-text">
            <div class="riw-summary-value">{{ formatNumber(card.value) }}</div>
            <div class="riw-summary-label">{{ card.label }}</div>
          </div>
        </div>
      </div>
      <div class="riw-preview__toolbar">
        <div class="riw-seg" role="group" aria-label="Filter rows">
          <button type="button" :aria-pressed="String(filter === 'all')" @click="filter = 'all'">All rows</button>
          <button type="button" :aria-pressed="String(filter === 'ok')" @click="filter = 'ok'">Will import</button>
          <button type="button" :aria-pressed="String(filter === 'skip')" @click="filter = 'skip'">
            Will skip<template v-if="counts.skipped"> ({{ formatNumber(counts.skipped) }})</template>
          </button>
        </div>
        <!-- Facility filter: only when rows are assigned by Facility; combined (AND) with the row filter.
             Attached in place (not teleported) so Tab moves from the trigger into the checkboxes and the
             dialog's focus trap keeps them reachable; Escape and an outside click close it. -->
        <div v-if="facilityFilter" class="riw-fac-filter">
          <v-menu
            v-model="facilityMenuOpen"
            attach
            role="presentation"
            :close-on-content-click="false"
            offset-y
            nudge-bottom="4"
            min-width="220"
            max-width="360"
            max-height="300"
            content-class="riw riw-fac-menu">
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="riw-fac-trigger"
                :class="{ 'is-set': facilityActive }"
                v-bind="attrs"
                :aria-label="`Facility filter: ${facilityLabel}`"
                title="Filter rows by Facility"
                v-on="on">
                <v-icon size="17" class="riw-fac-trigger__icon">mdi-domain</v-icon>
                <span class="riw-fac-trigger__val">{{ facilityLabel }}</span>
                <v-icon size="18" class="riw-fac-trigger__chev">mdi-chevron-down</v-icon>
              </button>
            </template>
            <div class="riw-fac-menu__list" role="group" aria-label="Show rows for these Facilities">
              <label class="riw-fac-item riw-fac-item--all">
                <input
                  type="checkbox"
                  :checked="allFacilitiesState === 'all'"
                  :indeterminate.prop="allFacilitiesState === 'some'"
                  @change="onToggleAllFacilities" />
                <span class="riw-fac-item__name">All Facilities</span>
                <span class="riw-fac-item__meta">{{ formatNumber(rows.length) }}</span>
              </label>
              <div class="riw-fac-sep" role="presentation"></div>
              <label
                v-for="option in facilityFilter.options"
                :key="option.key"
                class="riw-fac-item"
                :class="{ 'riw-fac-item--none': option.none }">
                <input
                  type="checkbox"
                  :checked="selectedFacilities.includes(option.key)"
                  @change="onToggleFacility(option.key, $event.target.checked)" />
                <v-icon size="16">{{ option.none ? 'mdi-domain-off' : 'mdi-domain' }}</v-icon>
                <span class="riw-fac-item__name" :title="option.label">
                  {{ option.label }}<span v-if="option.none" class="riw-fac-item__skip"> (skipped)</span>
                </span>
                <span class="riw-fac-item__meta">{{ formatNumber(option.rows) }}</span>
              </label>
            </div>
          </v-menu>
        </div>
        <span class="riw-preview__spacer"></span>
        <span class="riw-preview__note">
          <v-icon size="14">mdi-information-outline</v-icon>
          <span>{{ note }}</span>
        </span>
      </div>
    </div>

    <div class="riw-table-wrap">
      <div class="riw-table-card">
        <div class="riw-table-scroll">
          <table class="riw-data-table">
            <thead>
              <tr>
                <th class="riw-data-table__rownum riw-data-table__sticky">Row</th>
                <th v-if="identity" class="riw-data-table__sticky riw-data-table__sticky-id" :style="stickyIdStyle">
                  {{ identity.label }} <v-icon size="13">mdi-fingerprint</v-icon>
                  <span class="riw-data-table__type">{{ identity.type }} · identity</span>
                </th>
                <th>Status</th>
                <th v-for="col in otherColumns" :key="col.key" :class="{ 'is-num': col.numeric }">
                  {{ col.label }}<span class="riw-data-table__type">{{ col.type }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in visibleRows" :key="item.index" :class="{ 'is-skip': item.issue }">
                <td class="riw-data-table__rownum riw-data-table__sticky">{{ item.index + 2 }}</td>
                <td
                  v-if="identity"
                  class="riw-mono riw-data-table__sticky riw-data-table__sticky-id"
                  :class="{ 'is-bad': isBad(item, identity.key) }"
                  :style="stickyIdStyle">
                  <template v-if="!isBlank(item.row[identity.key])">{{ item.row[identity.key] }}</template>
                  <span v-else class="riw-dash">—</span>
                </td>
                <td>
                  <span v-if="item.issue" class="riw-status riw-status--skip" :title="item.issue.label">
                    <v-icon size="14">mdi-close-circle-outline</v-icon>Skip · {{ item.issue.label }}
                  </span>
                  <span v-else class="riw-status riw-status--ok"><v-icon size="14">mdi-check-circle-outline</v-icon>Import</span>
                </td>
                <td
                  v-for="col in otherColumns"
                  :key="col.key"
                  :class="{ 'is-num': col.numeric, 'is-bad': isBad(item, col.key) }"
                  :title="cellTitle(item, col)">
                  <template v-if="col.facility && !isBlank(item.row[col.key])">
                    <span class="riw-mono">{{ item.row[col.key] }}</span>
                    <span v-if="mappedFacility(item.row[col.key])" class="riw-dash"> → {{ mappedFacility(item.row[col.key]) }}</span>
                  </template>
                  <template v-else-if="!isBlank(item.row[col.key])">{{ item.row[col.key] }}</template>
                  <span v-else class="riw-dash">—</span>
                </td>
              </tr>
              <tr v-if="!visibleRows.length">
                <td :colspan="otherColumns.length + (identity ? 3 : 2)" class="riw-data-table__empty">
                  {{ emptyText }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="capText" class="riw-preview__cap" aria-live="polite">{{ capText }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { isBlankCell } from '../../../modules/registerImportWizardModel';
import {
  allFacilitiesState,
  facilityFilterLabel,
  facilityFilterSignature,
  isFacilityFilterActive,
  previewCapText,
  selectedFacilityKeys,
  toggleAllFacilities,
  toggleFacilityKey,
} from './facilityPreviewFilter';

/** Rows rendered at most; the counts always cover every parsed row (IMP-9). */
const PREVIEW_RENDER_LIMIT = 500;
const ROWNUM_WIDTH_PX = 64;

/**
 * In-place Preview for the Map Fields step (Flow A — the rail Config / Preview switch). Shows every
 * parsed row's predicted outcome with the current mapping applied. Predictions mirror the server
 * (registerImportWizardModel.evaluateRows); new vs. updated is only known after the import.
 */
export default {
  name: 'ImportPreviewView',
  props: {
    rows: { type: Array, default: () => [] },
    /** evaluateRows(...).issues — aligned with rows. */
    issues: { type: Array, default: () => [] },
    /** evaluateRows(...).counts */
    counts: { type: Object, required: true },
    /** [{ key, label, type, numeric, identity, facility }] in display order. */
    columns: { type: Array, default: () => [] },
    /** Show the "Facility not matched" card (Facility assignment applies). */
    facilityApplies: { type: Boolean, default: false },
    /** raw value → member Facility name when it is mapped by alias; null otherwise. */
    aliasTargetOf: { type: Function, default: () => null },
    identityLabel: { type: String, default: '' },
    /**
     * Facility filter model (facilityPreviewFilter.buildFacilityFilterModel): { rowKeys aligned with
     * rows, options }. Null hides the filter — rows are not assigned by Facility, or the Facility
     * Group's Facilities are not loaded yet (rows could not be resolved).
     */
    facilityFilter: { type: Object, default: null },
  },
  data: () => ({
    filter: 'all',
    /** null = every Facility; otherwise the selected option keys. */
    facilitySelection: null,
    facilityMenuOpen: false,
  }),
  computed: {
    identity() {
      return this.columns.find((col) => col.identity) || null;
    },
    otherColumns() {
      return this.columns.filter((col) => !col.identity);
    },
    stickyIdStyle() {
      return { left: `${ROWNUM_WIDTH_PX}px` };
    },
    facilityOptions() {
      return this.facilityFilter ? this.facilityFilter.options : [];
    },
    selectedFacilities() {
      return selectedFacilityKeys(this.facilitySelection, this.facilityOptions);
    },
    facilityActive() {
      return !!this.facilityFilter && isFacilityFilterActive(this.facilitySelection, this.facilityOptions);
    },
    allFacilitiesState() {
      return allFacilitiesState(this.facilitySelection, this.facilityOptions);
    },
    facilityLabel() {
      return facilityFilterLabel(this.facilitySelection, this.facilityOptions);
    },
    facilitySignature() {
      return facilityFilterSignature(this.facilityOptions);
    },
    filtered() {
      const list = [];
      const facilityKeys = this.facilityActive ? new Set(this.selectedFacilities) : null;
      const rowKeys = facilityKeys ? this.facilityFilter.rowKeys : null;
      this.rows.forEach((row, index) => {
        const issue = this.issues[index] || null;
        if (this.filter === 'ok' && issue) return;
        if (this.filter === 'skip' && !issue) return;
        if (facilityKeys && !facilityKeys.has(rowKeys[index])) return;
        list.push({ row: row || {}, index, issue });
      });
      return list;
    },
    capText() {
      return previewCapText({
        visible: this.visibleRows.length,
        matched: this.filteredCount,
        total: this.rows.length,
        filtered: this.filter !== 'all' || this.facilityActive,
      });
    },
    emptyText() {
      if (this.facilityActive) return 'No rows for the selected Facilities.';
      return this.filter === 'skip' ? 'No rows will be skipped.' : 'No rows to show.';
    },
    filteredCount() {
      return this.filtered.length;
    },
    visibleRows() {
      return this.filtered.slice(0, PREVIEW_RENDER_LIMIT);
    },
    cards() {
      const c = this.counts;
      const list = [
        { key: 'total', label: 'Rows in file', value: c.total, icon: 'mdi-table-row', color: 'blue' },
        { key: 'ready', label: 'Will import', value: c.ready, icon: 'mdi-check-circle-outline', color: 'primary' },
        { key: 'mandatory', label: 'Missing mandatory', value: c.mandatory, icon: 'mdi-asterisk', color: c.mandatory ? 'amber' : 'violet' },
        { key: 'dup', label: 'Duplicate identity', value: c.duplicateIdentity, icon: 'mdi-fingerprint-off', color: c.duplicateIdentity ? 'red' : 'violet' },
      ];
      if (this.facilityApplies) {
        list.push({ key: 'facility', label: 'Facility not matched', value: c.facility, icon: 'mdi-domain-off', color: c.facility ? 'amber' : 'violet' });
      }
      return list;
    },
    note() {
      const parts = [];
      parts.push(this.identityLabel
        ? `Rows with the same ${this.identityLabel} are updated; the rest are added. The counts appear after import.`
        : 'Without an identity column every row is added; none can be updated later.');
      if (this.identityLabel && this.counts.noIdentity) {
        parts.push(`${this.formatNumber(this.counts.noIdentity)} rows have no ${this.identityLabel} value — they import but can’t be updated later.`);
      }
      return parts.join(' ');
    },
  },
  watch: {
    // A Facility column or name-mapping change regroups the rows: start again from all Facilities.
    facilitySignature() {
      this.facilitySelection = null;
    },
  },
  methods: {
    isBlank: isBlankCell,
    onToggleFacility(key, checked) {
      this.facilitySelection = toggleFacilityKey(this.facilitySelection, this.facilityOptions, key, checked);
    },
    onToggleAllFacilities() {
      this.facilitySelection = toggleAllFacilities(this.facilitySelection, this.facilityOptions);
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString('en-US');
    },
    isBad(item, key) {
      return !!item.issue && item.issue.column === key;
    },
    mappedFacility(raw) {
      return this.aliasTargetOf(raw);
    },
    cellTitle(item, col) {
      if (this.isBad(item, col.key)) return item.issue.label;
      const value = item.row[col.key];
      return isBlankCell(value) ? null : String(value);
    },
  },
};
</script>
